<script setup lang="ts">
import type { ESpecialPage } from '@base/utils/constants'
import { ECarts } from '@base/utils/constants'
import type * as pf from '@amirada/lib-shared'
import { PfCmdType } from '@amirada/lib-shared'
import { useAppIndexStore } from '@base/stores/indexStore'
import { useCartStore } from '@base/stores/cartStore'
import { ut_getText, ut_isEmbedded, ut_returnNewRoute } from '@base/utils/utils'
import type { EIconSize } from '~/utils/constants'
import { ECartIconAr, ECartPageText } from '~/utils/constants'
import {
  ut_wilo_addItemToCart,
  ut_wilo_isEcom,
  ut_wilo_removeItemFromCart,
  ut_wilo_setAdessoStorage, ut_wilo_showPriceList,
} from '~/utils/utils'
import { usePfinderAPI } from '@base/composables/pfinderAPI'
import { priceListSelectionKey } from '~/composables/priceListCart'

const p = defineProps<{
  item?: pf.TPfNode
  config?: pf.TPfNode
  size?: EIconSize
  inactiveColor?: string
  namePath?: string
  nodeName?: string
  isSideBar?: boolean
  isList?: boolean
  isCard?: boolean
  isProduct?: boolean
  isCart?: boolean
  isCompareTable?: boolean
  isCompare?: boolean
  isInternal?: boolean
  isOld?: boolean
  realCountry: string
  showPdf?: boolean
}>()

const emit = defineEmits<{
  'update:buy': [string]
  'update:showModalPdf': [boolean]
}>()

const appConfig = useAppConfig()
const indexStore = useAppIndexStore()
const cartStore = useCartStore()
const pfinderAPI = usePfinderAPI()

// Inside the catalog modal only: the modal's selection. The price-list icon marks a tile there and does
// not write the cart — see CartCatalogModal. Its presence IS "we are in the catalog modal".
const selection = inject(priceListSelectionKey, undefined)

function showIcon(cart: ECarts): boolean {
  let res: boolean = true
  // Catalog modal: only the price-list icon, all other carts hidden (the price-list branch below still
  // applies its own showPriceList/isProduct gating).
  if (selection && cart !== ECarts.ct_price_list_create) {
    return false
  }
  if (cart === ECarts.ct_price_list_create) {
    // Only in the catalog modal (24.09.2026). A price list is built in its project, and a series may
    // stand there more than once — an add/remove toggle on a product page cannot say which row it
    // means, and adding from outside cannot know where.
    if (!selection || !ut_wilo_showPriceList(p.isInternal) || p.isProduct) {
      res = false
    }
  }
  else if (cart === ECarts.ct_catalogue_create && !(p.isCard || p.isList)) {
    res = !ut_isEmbedded()
  }
  if (ut_isEmbedded() && (cart === ECarts.ct_download_list || cart === ECarts.ct_favourites || cart === ECarts.ct_catalogue_create)) {
    res = false
  }
  // Don't show add to cart icon for special pages
  else if (cart === ECarts.ct_shopping_cart && !p.isList && (!p.isProduct || (p.isProduct && !p.isCompareTable))) {
    res = false
  }
  else if (cart === ECarts.ct_product_compare && p.isCompare) {
    res = false
  }
  else if (cart === ECarts.ct_product_compare || cart === ECarts.ct_download_list || cart === ECarts.ct_shopping_cart) {
    const attrs: Record<string, string> = p.item?.attrs || {}
    if (cart === ECarts.ct_product_compare) {
      res = !(p.isProduct ? !attrs['TCT'] : !attrs['SCT'])
      if (res) { // check COA attribut Compare Active or is _preview
        res = !!attrs['COA'] || indexStore.state.paramData.rcco === '_preview' // Compare test mode show only in preview
      }
    }
    else if (cart === ECarts.ct_download_list) {
      res = !(p.isProduct ? true : !attrs['DWT'])
    }
    else if (cart === ECarts.ct_shopping_cart) {
      res = (attrs['ECOM'] !== '' || attrs['WHO'] !== '')
      if (res) {
        res = isShoppingAllowed(attrs)
      }
    }
  }
  return res
}

function isShoppingAllowed(attrs: Record<string, string>): boolean {
  return (ut_wilo_isEcom(attrs, p.realCountry)) || (';' + (attrs['WHO'] || '').toLowerCase() + ';').includes(';' + p.realCountry + ';')
}

// Shown as active: in the modal, marked there; everywhere else, in the cart.
function isActive(cart: ECarts): boolean {
  if (cart === ECarts.ct_price_list_create && selection) {
    return selection.isSelected(p.namePath || '')
  }
  return cartStore.getIsInCart(cart, p.namePath || '', p.nodeName || '')
}

async function atClick(cart: ECarts) {
  // Catalog modal: mark or unmark the tile — the cart is written on "accept", as new rows.
  if (cart === ECarts.ct_price_list_create && selection && p.namePath) {
    selection.toggle(p.namePath)
    return
  }
  // One bracket for the whole click. The early return below used to leave the overlay on — the page
  // stayed locked whenever a product had no namePath (14.10.2026).
  await indexStore.withLoading(async () => {
    let namePath: string = p.namePath || ''
    // If no namepath is given search nodeByName
    if (!p.namePath && p.nodeName) {
      const res = await pfinderAPI.fetchData(PfCmdType.SEARCH, { term: '$name:' + p.nodeName }, { paramData: indexStore.state.paramData, noUrlParams: true })
      namePath = res?.result?.found[0]?.namePaths?.[0] || ''
      if (!namePath) { return }
    }
    if (cart !== ECarts.ct_shopping_cart) {
      if (!cartStore.getIsInCart(cart, namePath, '')) {
        await ut_wilo_addItemToCart(cartStore, cart, !p.isProduct, namePath)
      }
      else {
        await ut_wilo_removeItemFromCart(cartStore, cart, !p.isProduct, namePath)
      }
      ut_wilo_setAdessoStorage(cart, cartStore.getCartCount(cart), p.realCountry)
    // shop online
    }
    else {
      emit('update:buy', namePath)
    }
  })
}

// Modify link to real country
function specialPagePath(page: ESpecialPage | ECarts) {
  const path: string = '/' + [p.realCountry, indexStore.state.paramData.lcda, indexStore.getHiddenRootPathSlugs, page].join('/')
  return ut_returnNewRoute(path, {}, '', { noTab: true })
}
</script>

<template>
  <!--  Sidebar -->
  <div v-if="isSideBar" class="flex flex-wrap gap-2 lg:gap-0 lg:bg-secondary w-full lg:w-min">
    <template v-for="(cart, index) of appConfig.listArType" :key="cart">
      <template v-if="cart !== ECarts.ct_shopping_cart && !(cart === ECarts.ct_price_list_create && !ut_wilo_showPriceList(p.isInternal))">
        <div class="py-0 px-1 lg:py-4 lg:px-4">
          <UTooltip :text="ut_getText('def_' + cart + '_txt_page')">
            <UChip :text="cartStore.getCartCount(cart)" :show="cartStore.getCartCount(cart) > 0" color="tertiary">
              <UButton :icon="'i-wilo-' + (ECartIconAr[cart] || '')" color="secondary" :to="specialPagePath(cart)" :aria-label="ut_getText('def_' + cart + '_txt_page')" />
            </UChip>
          </UTooltip>
        </div>
        <hr v-if="index < 3" class="hidden lg:block text-white mx-auto w-8/10 my-0 p-0">
      </template>
    </template>
  </div>

  <template v-else>
    <!-- Create PDF -->
    <template v-if="showPdf">
      <UTooltip
        :text="ut_getText('label_download_datasheet')"
      >
        <UButton
          v-if="isList"
          variant="shadow"
          color="light"
          icon="i-wilo-file-arrow-down-solid-full"
          aria-label="PDF"
          @click="emit('update:showModalPdf', true)"
        />
        <UButton
          v-else
          variant="shadow"
          color="tertiary"
          icon="i-wilo-file-arrow-down-solid-full"
          aria-label="PDF"
          label="PDF"
          @click="emit('update:showModalPdf', true)"
        />
      </UTooltip>
    </template>

    <!-- List Icons -->
    <template v-if="!isOld">
      <template v-for="cart of appConfig.listArType" :key="cart">
        <template v-if="showIcon(cart)">
          <UTooltip
            :text="cart === ECarts.ct_shopping_cart ? ut_getText('txt_buy-external-button') : ut_getText(isActive(cart) ? 'txt_remove-from-' + ECartPageText[cart] : 'txt_add-to-' + ECartPageText[cart])"
          >
            <UButton
              v-if="isList || isCard"
              variant="shadow"
              :color="isActive(cart) ? 'primary' : 'light'"
              :icon="'i-wilo-' + (ECartIconAr[cart] || '')"
              :aria-label="ut_getText('def_' + cart + '_txt_page')"
              @click="atClick(cart)"
            />

            <UButton
              v-else
              variant="shadow"
              :color="isActive(cart) ? 'primary' : 'tertiary'"
              :icon="'i-wilo-' + (ECartIconAr[cart] || '')"
              :label="ut_getText('def_' + cart + '_txt_page')"
              @click="atClick(cart)"
            />
          </UTooltip>
        </template>
      </template>
    </template>
  </template>
</template>
